import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { RotateCcw } from 'lucide-react'
import StudioImage, { CENTRED, isCentred, type Framing } from './StudioImage'
import type { PlacementShape } from '../api/media'

/**
 * The shapes a photograph actually appears in.
 *
 * <b>Previewed together, which is the whole point.</b> A single cropper shows
 * one box and lets somebody pick a point that ruins the others — and the same
 * photo really does appear as a wide banner, a card and a thumbnail. Seeing all
 * three move at once is what makes a good choice obvious rather than lucky.
 */
const SHAPES: { label: string; className: string }[] = [
  { label: 'Wide', className: 'aspect-[16/6]' },
  { label: 'Card', className: 'aspect-[16/9]' },
  { label: 'Square', className: 'aspect-square' },
]

/*
 * Sized by height, not width, so the three sit in one row and can actually be
 * compared. They were `w-full` and stacked, each nearly a thousand pixels wide
 * — which defeats the reason for showing three: seeing them move together is
 * the whole point, and you cannot see them together if comparing two means
 * scrolling (D-253, amending the presentation D-199 shipped).
 */
const GENERIC_HEIGHT = 'h-20'
const REAL_HEIGHT = 'h-44'

/**
 * Positioning a photograph by dragging it (D-199).
 *
 * Drag is the primary gesture — the owner works on a Mac laptop and a Mac at
 * the studio, so a trackpad is always there. Arrow keys do the same job for
 * anyone not using one, because a control that only responds to a pointer is
 * one some people simply cannot operate.
 *
 * <b>Nothing here is required.</b> Centred at zoom 1 is what every photograph
 * does already, so this is only ever opened when something sits badly.
 */
export default function FramingEditor({
  src,
  alt,
  value,
  onChange,
  onReset,
  scope,
  shape,
  liveImmediately = false,
}: {
  src: string
  alt: string
  value: Framing
  onChange: (framing: Framing) => void
  /** Back to the photograph's own framing, or to centre when this *is* it. */
  onReset?: () => void
  /** What is being framed — "everywhere this photo is used", or one place. */
  scope: string
  /**
   * The shape this photograph is actually used in, previewed first.
   *
   * <b>The generic three stay when one is given.</b> D-199's argument for
   * showing several at once is that a point flattering one crop ruins another,
   * and that holds hardest for a photograph used in two placements — the
   * shape being edited is the one that matters, not the only one that exists.
   */
  shape?: PlacementShape | null
  /** Said plainly when true: this is live the moment it is dragged (D-253). */
  liveImmediately?: boolean
}) {
  const stage = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState(false)


  /*
   * The focal point follows the pointer directly: where you put it in the
   * preview is what the crop keeps. Inverted dragging ("move the image") reads
   * naturally for about half of people and backwards for the rest, and there is
   * no arrangement that suits both — so this picks the one that can be
   * described in a sentence.
   */
  const pointTo = (event: { clientX: number; clientY: number }) => {
    const box = stage.current?.getBoundingClientRect()
    if (!box) return

    onChange({
      ...value,
      focalX: clamp(((event.clientX - box.left) / box.width) * 100),
      focalY: clamp(((event.clientY - box.top) / box.height) * 100),
    })
  }

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    // Capture, so a drag that leaves the box keeps working instead of sticking
    // wherever the pointer crossed the edge.
    event.currentTarget.setPointerCapture(event.pointerId)
    setDragging(true)
    pointTo(event)
  }

  const nudge = (dx: number, dy: number) =>
    onChange({ ...value, focalX: clamp(value.focalX + dx), focalY: clamp(value.focalY + dy) })

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[12px] text-ink-3">
        Drag to choose what stays in frame &mdash; {scope}.
      </p>

      <div
        ref={stage}
        role="application"
        aria-label="Drag to position the photograph"
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={(e) => dragging && pointTo(e)}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 10 : 2
          if (e.key === 'ArrowLeft') nudge(-step, 0)
          else if (e.key === 'ArrowRight') nudge(step, 0)
          else if (e.key === 'ArrowUp') nudge(0, -step)
          else if (e.key === 'ArrowDown') nudge(0, step)
          else return

          e.preventDefault()
        }}
        className={`relative aspect-[16/9] w-full cursor-grab overflow-hidden rounded-xl border border-line-2 ${
          dragging ? 'cursor-grabbing' : ''
        }`}
      >
        {/* The whole photograph, uncropped, so it is clear what is available. */}
        <img src={src} alt={alt} className="size-full object-contain" draggable={false} />

        {/* Where the crop is anchored. */}
        <span
          aria-hidden="true"
          style={{ left: `${value.focalX}%`, top: `${value.focalY}%` }}
          className="pointer-events-none absolute size-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-accent bg-accent/20"
        />
      </div>

      <label className="flex items-center gap-3">
        <span className="text-[12px] font-semibold text-ink-3">Zoom</span>
        <input
          type="range"
          min={1}
          max={3}
          step={0.05}
          value={value.zoom}
          onChange={(e) => onChange({ ...value, zoom: Number(e.target.value) })}
          className="flex-1 accent-[var(--color-accent)]"
        />
        <span className="w-10 text-right text-[12px] text-ink-3 tabular">
          {value.zoom.toFixed(2)}&times;
        </span>
      </label>

      {/*
        The shape this photograph is genuinely used in, first and marked as
        such. Its three generic siblings below matched no block on the site,
        so a crop could be chosen against a 16:6 banner and land in a 3:4
        portrait (D-253).
      */}
      {shape && (
        /*
          `w-fit`, or the aspect ratio does nothing. `StudioImage` renders a
          block-level span, and a block with a resolved height still stretches
          to its container's width — so `aspect-[3/4] h-44` rendered a wide
          box. The generic previews escape this only because they are flex
          items, which shrink to content.
        */
        <div className="flex w-fit flex-col gap-1">
          <span className="text-[11px] font-semibold text-accent-2">{shape.label}</span>
          <StudioImage
            src={src}
            alt=""
            framing={value}
            loading="eager"
            className={`${shape.className} ${REAL_HEIGHT} rounded-lg border-2 border-accent`}
          />
        </div>
      )}

      {/*
        Every shape at once. This is what a single-box cropper cannot tell her:
        that the point which flatters the banner cuts a head off the thumbnail.
      */}
      <div className="flex flex-wrap items-end gap-3">
        {SHAPES.map((generic) => (
          <div key={generic.label} className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-ink-3">
              {shape ? `Also: ${generic.label.toLowerCase()}` : generic.label}
            </span>
            <StudioImage
              src={src}
              alt=""
              framing={value}
              loading="eager"
              className={`${generic.className} ${GENERIC_HEIGHT} rounded-lg border border-line`}
            />
          </div>
        ))}
      </div>

      {liveImmediately && (
        // Two controls in one card obeying opposite rules is fine if it is
        // said, and a trap if it is not — the words beside this are drafts
        // until Publish; this is not (D-253).
        <p className="text-[12px] text-ink-3">
          Positioning is live as soon as you drag it &mdash; it does not wait for Publish.
        </p>
      )}

      {onReset && !isCentred(value) && (
        <button
          type="button"
          onClick={onReset}
          className="inline-flex w-fit items-center gap-1.5 text-[12.5px] font-semibold text-ink-3 hover:text-ink"
        >
          <RotateCcw size={13} strokeWidth={2.5} />
          Reset
        </button>
      )}
    </div>
  )
}

export { CENTRED }

function clamp(value: number): number {
  return value < 0 ? 0 : value > 100 ? 100 : Math.round(value * 10) / 10
}
